import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { signOut } from 'firebase/auth';

import { auth } from '../firebaseAuth';
import { getAuthHeaders } from '../utils/authHeaders';
import { getBrowserVerificationHeaders } from '../utils/appCheck';
import { resolveFrontendApiBase } from '../utils/apiBase';
import { AUTH_STAGE_TIMEOUT_MS, runAuthStage } from '../utils/authTelemetry';
import { isFinancialWrite, prepareFinancialOperation } from './financialOperation';
import {
  API_SESSION_EXPIRED_EVENT,
  createApiAuthResponseErrorHandler,
  isApiRequestConfig,
  type AuthRetryConfig,
} from '../utils/apiAuthRecovery';

const apiBase = resolveFrontendApiBase();
const adminAxios = axios.create({ baseURL: apiBase });

type AdminAxiosTimingConfig = InternalAxiosRequestConfig & {
  _requestStartedAtMs?: number;
  _financialOperationStorageKey?: string;
}

const isRevenueHealthGet = (config?: InternalAxiosRequestConfig | null): boolean =>
  Boolean(
    config?.method?.toLowerCase() === 'get' &&
    typeof config?.url === 'string' &&
    config.url.includes('/admin/revenue-health')
  );

const adminAuthErrorHandler = createApiAuthResponseErrorHandler({
  apiBase,
  getCurrentUser: () => auth.currentUser,
  replayRequest: config => adminAxios.request(config as InternalAxiosRequestConfig),
  setAuthorizationHeader: (config: AuthRetryConfig, token: string) => {
    const headers = AxiosHeaders.from(config.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  },
  terminateSession: async () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(API_SESSION_EXPIRED_EVENT));
    }
    await runAuthStage(
      'session_sign_out',
      () => signOut(auth),
      { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signOut },
    );
  },
});

adminAxios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const timingConfig = config as AdminAxiosTimingConfig;
    timingConfig._requestStartedAtMs = Date.now();
    if (isRevenueHealthGet(config)) {
      console.info('[revenue-health] request started', {
        url: config.url,
        params: config.params,
        timeoutMs: config.timeout,
      });
    }
    if (!isApiRequestConfig(config, apiBase)) {
      throw new Error('Admin requests require the configured API origin and path.');
    }
    const headers = AxiosHeaders.from(config.headers || {});
    const target = new URL(config.url || '', new URL(config.baseURL || apiBase, window.location.origin));
    if (isFinancialWrite(config.method, target.pathname) && !headers.has('Idempotency-Key')) {
      if (!auth.currentUser) throw new Error('Sign in before adjusting credits.');
      const prepared = await prepareFinancialOperation(auth.currentUser.uid, target.pathname, config.data);
      headers.set('Idempotency-Key', prepared.operationId);
      timingConfig._financialOperationStorageKey = prepared.storageKey;
    }
    if (!headers.has('Authorization')) {
      const authHeaders = await getAuthHeaders();
      Object.entries(authHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
    } else if (!headers.has('X-Firebase-AppCheck')) {
      Object.entries(await getBrowserVerificationHeaders()).forEach(([key, value]) => headers.set(key, value));
    }
    config.headers = headers;
    return config;
  },
  (error) => Promise.reject(error)
);

adminAxios.interceptors.response.use(
  (response) => {
    const timingConfig = response.config as AdminAxiosTimingConfig;
    if (timingConfig._financialOperationStorageKey && response.status !== 202 && !response.data?.pending) {
      sessionStorage.removeItem(timingConfig._financialOperationStorageKey);
    }
    if (isRevenueHealthGet(response.config)) {
      console.info('[revenue-health] request completed', {
        url: response.config.url,
        params: response.config.params,
        status: response.status,
        durationMs: timingConfig._requestStartedAtMs ? Date.now() - timingConfig._requestStartedAtMs : null,
      });
    }
    return response;
  },
  async (error) => {
    const config = error?.config as AdminAxiosTimingConfig | undefined;
    const detail = error?.response?.data?.detail;
    if (isRevenueHealthGet(config)) {
      console.warn('[revenue-health] request failed', {
        url: config?.url,
        params: config?.params,
        status: error?.response?.status ?? null,
        code: error?.code ?? null,
        detail,
        durationMs: config?._requestStartedAtMs ? Date.now() - config._requestStartedAtMs : null,
      });
    }
    return adminAuthErrorHandler(error);
  }
);

export default adminAxios;
